import React from 'react';

import { GatherUsingAINodeState, NodeField, SpeakTextNodeState } from '@components/Flow/Nodes';
import { Command, tabActions } from '@store/index';
import { Alert, Stack } from '@mui/material';
import { useDispatch } from 'react-redux';
import { Label } from '@common/Label';
import { TTSVoiceAutocomplete } from '@common/Autocomplete/TTSVoiceAutocomplete';
import { NodeInputExpression } from '../../NodeInput';
import { Select } from '@common/Select';
import { getTTSVoices } from '@requests/telnyx/tts-voices.requests';
import { getTTSVoiceParams, TTSVoice } from '@requests/telnyx';
import { useQuery } from '@tanstack/react-query';
import { RequestError } from '@requests/common';

interface Props {
  command: Command;
  fields: NodeField[];
}

export const ElevenLabsVoice: React.FC<Props> = ({ command, fields }) => {
  const dispatch = useDispatch();

  const supportedCommands = ['call.ai_gather', 'call.speak'];
  if (!supportedCommands.includes(command?.config?.command_id)) {
    throw new Error(
      'This node does not support using the ElevenLabs voice. The ElevenLabsVoice component should be updated to allow it.'
    );
  }

  const elevenlabs_api_key = command?.state?.elevenlabs_api_key;

  const { data: options = [], isLoading } = useQuery<TTSVoice[], RequestError, TTSVoice[], [string, getTTSVoiceParams]>(
    {
      queryKey: ['tts-voices', { provider: 'elevenlabs', elevenlabs_api_key_ref: elevenlabs_api_key }],
      queryFn: getTTSVoices,
      retry: false,
      enabled: Boolean(elevenlabs_api_key),
    }
  );

  const state = command.state as SpeakTextNodeState | GatherUsingAINodeState;
  const isElevenLabsVoice = state?.voice?.value === 'ElevenLabs';
  if (!isElevenLabsVoice) return null;

  const elevenlabs_voice_id = state.elevenlabs_voice_id;
  const elevenlabs_model_id = state.elevenlabs_model_id;

  const elevenlabsVoiceId = fields.find((field) => field.id === 'elevenlabs_voice_id');
  const elevenlabsApiKey = fields.find((field) => field.id === 'elevenlabs_api_key');
  const elevenlabsModelId = fields.find((field) => field.id === 'elevenlabs_model_id');

  const voiceOptions = options.filter((item) => item.provider === 'elevenlabs');
  const selectedVoice = voiceOptions.find((item) => item.value === elevenlabs_voice_id);

  const updateField = (value: string) => {
    if (!elevenlabsVoiceId) return;

    const nodeId = command.node.id;

    dispatch(tabActions.nodeInputUpdate({ nodeId, fieldId: elevenlabsVoiceId.id, value }));
    if (elevenlabsModelId) dispatch(tabActions.nodeInputUpdate({ nodeId, fieldId: elevenlabsModelId.id, value: '' }));
    dispatch(tabActions.setSaved(false));
  };

  const updateVoiceModelId = (value: string) => {
    if (!elevenlabsModelId) return;

    const nodeId = command.node.id;

    dispatch(tabActions.nodeInputUpdate({ nodeId, fieldId: elevenlabsModelId.id, value }));
    dispatch(tabActions.setSaved(false));
  };

  return (
    <Stack spacing='1.25rem'>
      {elevenlabsApiKey && <NodeInputExpression command={command} field={elevenlabsApiKey} />}
      {!elevenlabs_api_key && (
        <Alert severity='info'>Please select an ElevenLabs API key to view available voices.</Alert>
      )}
      {elevenlabsVoiceId && elevenlabs_api_key && (
        <Stack spacing={1}>
          <Label title={elevenlabsVoiceId?.label} tooltip={elevenlabsVoiceId?.description} />
          <TTSVoiceAutocomplete
            provider='elevenlabs'
            elevenlabs_api_key_ref={elevenlabs_api_key}
            value={elevenlabs_voice_id}
            onChange={(_, newValue) => newValue && updateField(newValue.value)}
            placeholder='ElevenLabs Voice ID'
          />
        </Stack>
      )}
      {elevenlabsModelId &&
        selectedVoice &&
        elevenlabs_api_key &&
        !isLoading &&
        selectedVoice?.high_quality_base_model_ids.length > 0 && (
          <Stack spacing={1} mt={1}>
            <Label
              title='ElevenLabs Model'
              tooltip='ElevenLabs models, identified by model IDs, determine the underlying capabilites of the voice model such as the languages they can speak in.'
            />
            <Select
              key='elevenlabs_model_id'
              value={elevenlabs_model_id || ''}
              setValue={(newValue) => updateVoiceModelId(newValue)}
              disabled={isLoading}
              placeholder='The ElevenLabs model to use with this voice.'
              options={
                selectedVoice?.high_quality_base_model_ids?.map((model) => ({ label: model, value: model })) || []
              }
            />
          </Stack>
        )}
    </Stack>
  );
};
